/**
 * Mobile Gallery Carousel - Inertial Swiper
 * Implements smooth touch/mouse drag sliding, momentum decay, and spring snapping for Shopify product media on mobile.
 */

class MobileGallery {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.product-gallery__track');
    this.slides = Array.from(container.querySelectorAll('.product-gallery__slide'));
    this.counterCurrent = container.querySelector('.product-gallery__counter-current');
    this.dots = Array.from(container.querySelectorAll('.product-gallery__dot'));
    this.thumbnails = Array.from(container.querySelectorAll('.product-gallery__thumb'));
    
    if (!this.track || this.slides.length === 0) return;

    // Check viewport width: only run touch swiper on Mobile (<= 768px)
    if (window.innerWidth > 768) {
      this.resetDesktopLayout();
      return;
    }

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationId = null;
    this.dragStartTime = 0;
    this.dragVelocity = 0;
    this.slideWidth = 0;
    this.maxTranslate = 0;
    
    this.init();
  }

  resetDesktopLayout() {
    // Clean up inline styles so vertical desktop stack scrolls naturally
    this.track.style.transform = '';
    this.track.style.transition = '';
    this.slides.forEach(slide => {
      slide.style.width = '';
    });
    
    // Set desktop sticky offset so on first image scroll, accordions align into view
    const details = document.querySelector('.mobile-pdp__details');
    if (details) {
      const buttons = details.querySelector('.mobile-pdp__buttons');
      if (buttons) {
        const updateOffset = () => {
          const offset = buttons.offsetTop;
          if (offset > 0) {
            details.style.setProperty('--pdp-top-offset', offset + 'px');
          }
        };
        updateOffset();
        window.addEventListener('resize', updateOffset, { passive: true });
      }
    }

    // Bind thumbnails click to scroll cleanly to target slide accounting for sticky header offset
    this.thumbnails.forEach((thumb, idx) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.thumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        
        const targetSlide = this.slides[idx];
        if (targetSlide) {
          const header = document.querySelector('.header-wrapper, header, .shopify-section-header');
          const headerHeight = header ? header.offsetHeight : 80;
          const targetTop = targetSlide.getBoundingClientRect().top + window.pageYOffset - (headerHeight + 20);
          
          window.scrollTo({
            top: targetTop,
            behavior: 'smooth'
          });
        }
      });
    });

    // IntersectionObserver to auto-update active thumbnail on scroll
    if ('IntersectionObserver' in window && !this._observer) {
      this._observer = new IntersectionObserver((entries) => {
        if (window.innerWidth <= 768) return;
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const slideIdx = this.slides.indexOf(entry.target);
            if (slideIdx !== -1) {
              this.thumbnails.forEach((t, i) => {
                t.classList.toggle('is-active', i === slideIdx);
              });
            }
          }
        });
      }, {
        threshold: 0.5
      });

      this.slides.forEach(slide => this._observer.observe(slide));
    }
  }

  init() {
    this.measureLayout();
    this.bindEvents();
    this.updateUI();
    
    // Listen for resize to recalculate widths or switch layout mode
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        this.resetDesktopLayout();
      } else {
        this.measureLayout();
        this.scrollToIndex(this.currentIndex, false);
      }
    });

    // Handle lazy loaded images inside slides
    this.slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (img) {
        img.addEventListener('load', () => {
          if (window.innerWidth <= 768) this.measureLayout();
        });
      }
    });
  }

  measureLayout() {
    this.slideWidth = this.container.getBoundingClientRect().width;
    this.maxTranslate = -(this.slides.length - 1) * this.slideWidth;
    
    this.slides.forEach(slide => {
      slide.style.width = `${this.slideWidth}px`;
    });
  }

  bindEvents() {
    // Touch Events
    this.track.addEventListener('touchstart', (e) => this.dragStart(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.dragMove(e), { passive: false });
    this.track.addEventListener('touchend', () => this.dragEnd());

    // Mouse Events (desktop browser testing fallback)
    this.track.addEventListener('mousedown', (e) => this.dragStart(e));
    this.track.addEventListener('mousemove', (e) => this.dragMove(e));
    this.track.addEventListener('mouseup', () => this.dragEnd());
    this.track.addEventListener('mouseleave', () => {
      if (this.isDragging) this.dragEnd();
    });

    // Thumbnails click (on mobile: slides carousel)
    this.thumbnails.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          this.scrollToIndex(idx);
        }
      });
    });
  }

  dragStart(e) {
    this.isDragging = true;
    this.startX = this.getPositionX(e);
    this.dragStartTime = performance.now();
    this.prevTranslate = this.currentTranslate;
    
    this.track.style.transition = 'none';
    cancelAnimationFrame(this.animationId);
  }

  dragMove(e) {
    if (!this.isDragging) return;
    
    const currentX = this.getPositionX(e);
    const diff = currentX - this.startX;
    
    let targetTranslate = this.prevTranslate + diff;
    
    // Resistance edge drag
    if (targetTranslate > 0) {
      targetTranslate = targetTranslate * 0.35;
    } else if (targetTranslate < this.maxTranslate) {
      const overflow = targetTranslate - this.maxTranslate;
      targetTranslate = this.maxTranslate + (overflow * 0.35);
    }
    
    this.currentTranslate = targetTranslate;
    this.setTrackPosition();
    
    if (Math.abs(diff) > 5 && e.cancelable) {
      e.preventDefault();
    }
  }

  dragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    
    const dragDuration = performance.now() - this.dragStartTime;
    const dragDistance = this.currentTranslate - this.prevTranslate;
    
    this.dragVelocity = dragDistance / dragDuration;
    
    let targetIndex = this.currentIndex;
    const threshold = this.slideWidth * 0.20; // 20% swipe threshold
    
    if (Math.abs(dragDistance) > threshold || Math.abs(this.dragVelocity) > 0.3) {
      if (dragDistance < 0) {
        targetIndex = this.currentIndex + 1;
      } else {
        targetIndex = this.currentIndex - 1;
      }
    }
    
    targetIndex = Math.max(0, Math.min(targetIndex, this.slides.length - 1));
    this.scrollToIndex(targetIndex);
  }

  getPositionX(e) {
    return e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
  }

  scrollToIndex(index, smooth = true) {
    this.currentIndex = index;
    this.currentTranslate = -index * this.slideWidth;
    
    if (smooth) {
      this.track.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
    } else {
      this.track.style.transition = 'none';
    }
    
    this.setTrackPosition();
    this.updateUI();
  }

  setTrackPosition() {
    this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
  }

  updateUI() {
    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.currentIndex + 1;
    }
    
    this.dots.forEach((dot, idx) => {
      dot.classList.toggle('is-active', idx === this.currentIndex);
    });

    this.thumbnails.forEach((thumb, idx) => {
      thumb.classList.toggle('is-active', idx === this.currentIndex);
      if (idx === this.currentIndex && window.innerWidth <= 768) {
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });

    this.slides.forEach((slide, idx) => {
      const video = slide.querySelector('video');
      if (video) {
        if (idx === this.currentIndex) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    });
  }
}

// Immediate scanner functions to prevent Theme Editor async script loading race conditions
function initMobileGalleries() {
  const galleries = document.querySelectorAll('.product-gallery');
  galleries.forEach(gal => {
    if (!gal._galleryInstance) {
      gal._galleryInstance = new MobileGallery(gal);
    }
  });
}

// Run immediately upon script load
initMobileGalleries();

// Run on standard load
document.addEventListener('DOMContentLoaded', initMobileGalleries);

// Run on Shopify Customizer block/section updates
document.addEventListener('shopify:section:load', (e) => {
  const galleries = e.target.querySelectorAll('.product-gallery');
  galleries.forEach(gal => {
    gal._galleryInstance = new MobileGallery(gal);
  });
});
